import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import CircularProgress from '@mui/material/CircularProgress'
import Box from '@mui/material/Box'

import AuthLayout from '@/layouts/AuthLayout'
import MainLayout from '@/layouts/MainLayout'
import RequireAuth from '@/components/RequireAuth'
import RequireRole from '@/components/RequireRole'

// Pages — lazy loaded for route-based code splitting
const Login = lazy(() => import('@/pages/Login'))
const Dashboard = lazy(() => import('@/pages/Dashboard'))
const Nodes = lazy(() => import('@/pages/Nodes'))
const NodeDetail = lazy(() => import('@/pages/NodeDetail'))
const Users = lazy(() => import('@/pages/Users'))
const UserDetail = lazy(() => import('@/pages/UserDetail'))
const Stores = lazy(() => import('@/pages/Stores'))
const StoreDetail = lazy(() => import('@/pages/StoreDetail'))
const FloorPlansEditor = lazy(() => import('@/pages/FloorPlansEditor'))
const Catalogues = lazy(() => import('@/pages/Catalogues'))
const CatalogueDetail = lazy(() => import('@/pages/CatalogueDetail'))
const CatalogueCompare = lazy(() => import('@/pages/CatalogueCompare'))
const SearchLists = lazy(() => import('@/pages/SearchLists'))
const SearchListDetail = lazy(() => import('@/pages/SearchListDetail'))
const Inventories = lazy(() => import('@/pages/Inventories'))
const InventoryDetail = lazy(() => import('@/pages/InventoryDetail'))
const InventoryAdditionalOperations = lazy(() => import('@/pages/InventoryAdditionalOperations'))
const Alarms = lazy(() => import('@/pages/Alarms'))
const ItemSearch = lazy(() => import('@/pages/ItemSearch'))
const StatisticsCounters = lazy(() => import('@/pages/StatisticsCounters'))
const ImageSets = lazy(() => import('@/pages/ImageSets'))
const SipOperations = lazy(() => import('@/pages/SipOperations'))
const StatisticsSIP = lazy(() => import('@/pages/StatisticsSIP'))
const StatisticsEnvironmental = lazy(() => import('@/pages/StatisticsEnvironmental'))
const About = lazy(() => import('@/pages/About'))
const WidgetGenerator = lazy(() => import('@/pages/WidgetGenerator'))
const FloorPlanPublicView = lazy(() => import('@/pages/FloorPlanPublicView'))
const LibrariesDashboardPublicView = lazy(() => import('@/pages/LibrariesDashboardPublicView'))
const Notifications = lazy(() => import('@/pages/Notifications'))

const Loading = () => (
    <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
    </Box>
)

const App = () => (
    <Suspense fallback={<Loading />}>
        <Routes>
            <Route path='/occupancy/:token' element={<WidgetGenerator />} />
            {/* Alias retrocompatible: iframes antiguos embebidos antes de renombrar la ruta a /occupancy */}
            <Route path='/capacities/:token' element={<WidgetGenerator />} />
            <Route path='/capacities/:token/:storeCode' element={<WidgetGenerator />} />
            <Route path='/floor-plan-view/:token' element={<FloorPlanPublicView />} />
            <Route
                path='/libraries-dashboard-view/:token'
                element={<LibrariesDashboardPublicView />}
            />

            {/* Auth routes */}
            <Route element={<AuthLayout />}>
                <Route path='/login' element={<Login />} />
            </Route>

            {/* Protected routes */}
            <Route
                element={
                    <RequireAuth>
                        <MainLayout />
                    </RequireAuth>
                }>
                <Route path='/dashboard' element={<Dashboard />} />
                <Route path='/counters' element={<StatisticsCounters />} />
                <Route path='/alarms' element={<Alarms />} />
                <Route path='/nodes' element={<Nodes />} />
                <Route path='/nodes/:nodeId' element={<NodeDetail />} />
                <Route path='/inventories' element={<Inventories />} />
                <Route path='/inventories/:groupId' element={<InventoryDetail />} />
                <Route
                    path='/inventories/:groupId/additionalOperations'
                    element={<InventoryAdditionalOperations />}
                />
                <Route path='/catalogues' element={<Catalogues />} />
                <Route path='/catalogues/:catalogueId' element={<CatalogueDetail />} />
                <Route path='/catalogues/:catalogueId/compare' element={<CatalogueCompare />} />
                <Route path='/searchLists' element={<SearchLists />} />
                <Route path='/searchLists/:searchListId' element={<SearchListDetail />} />

                <Route path='/idModifications' element={<Navigate to='/stores' replace />} />
                <Route path='/itemEvents' element={<Navigate to='/itemSearch' replace />} />
                <Route path='/itemSearch' element={<ItemSearch />} />
                <Route path='/imagesets' element={<ImageSets />} />
                <Route path='/sipOperations' element={<SipOperations />} />
                <Route path='/sipStatistics' element={<StatisticsSIP />} />
                <Route path='/environmental' element={<StatisticsEnvironmental />} />
                <Route
                    path='/librariesDashboard'
                    element={<Navigate to='/dashboard?tab=libraries' replace />}
                />
                <Route path='/profile' element={<UserDetail />} />
                <Route element={<RequireRole allowedRoles={['admin', 'supervisor']} />}>
                    <Route path='/users' element={<Users />} />
                    <Route path='/users/:userId' element={<UserDetail />} />
                </Route>
                <Route path='/stores' element={<Stores />} />
                <Route path='/stores/:storeId' element={<StoreDetail />} />
                <Route path='/stores/:storeId/floor-plans' element={<FloorPlansEditor />} />
                <Route path='/widget-generator' element={<WidgetGenerator />} />
                <Route path='/about' element={<About />} />
                <Route path='/notifications' element={<Notifications />} />
            </Route>

            {/* Default redirect */}
            <Route path='*' element={<Navigate to='/dashboard' replace />} />
        </Routes>
    </Suspense>
)

export default App
